import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Row, Col, Card, Container, Alert, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { User as FaUser, Mail as FaEnvelope, Lock as FaLock, Calendar as FaCalendarAlt, Building2 as FaBuilding, FileText as FaFileAlt, MapPin as FaMapMarkerAlt, Building as FaCity, Phone as FaPhone, Trash2 as FaTrash, TriangleAlert as FaExclamationTriangle } from 'lucide-react';
import Loader from '../../components/Loader';
import { getUserDetails, updateUserProfile } from '../../actions/userActions';
import { USER_UPDATE_PROFILE_RESET } from '../../constants/userConstants';
import api from '../../api/axios';

function UserProfile() {
  const [name, setName] = useState('');
  const [lastName, setLastName] = useState(''); 
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localMessage, setLocalMessage] = useState('');
  const [birthDate, setBirthDate] = useState('');
  
  // Employer fields
  const [companyName, setCompanyName] = useState('');
  const [nip, setNip] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyCity, setCompanyCity] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  
  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userDetails = useSelector(state => state.userDetails);
  const { error: userDetailsError, loading, user } = userDetails;

  const userLogin = useSelector(state => state.userLogin);
  const { userInfo } = userLogin;

  const userUpdateProfile = useSelector(state => state.userUpdateProfile);
  const { success: updateSuccess, error: updateError } = userUpdateProfile;

  const isEmployer = user?.user_type === 'employer';

  useEffect(() => {
    if (!userInfo) {
      navigate('/login');
      return;
    }
    if (!user || !user.name || updateSuccess) {
      dispatch({ type: USER_UPDATE_PROFILE_RESET });
      dispatch(getUserDetails('profile'));
    } else {
      setName(user.name);
      setLastName(user.last_name || ''); 
      setEmail(user.email);
      setBirthDate(user.birth_date ? user.birth_date.substring(0, 10) : '');
      
      // Employer fields
      if (user.user_type === 'employer') {
        setCompanyName(user.company_name || '');
        setNip(user.nip || '');
        setCompanyAddress(user.company_address || '');
        setCompanyCity(user.company_city || '');
        setCompanyPhone(user.company_phone || '');
      }
    }
  }, [dispatch, userInfo, user, updateSuccess, navigate]);

  useEffect(() => {
    if (updateSuccess) {
      setLocalMessage('success');
      const timer = setTimeout(() => {
        setLocalMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    } else if (updateError) {
      setLocalMessage('error');
    }
  }, [updateSuccess, updateError]);

  const submitHandler = (e) => {
    e.preventDefault();
    setLocalMessage('');

    if (!name || !lastName || !email) {
      setLocalMessage('error');
      return;
    }

    // Walidacja formatu emaila
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setLocalMessage('invalid-email');
      return;
    }

    if (password && password !== confirmPassword) {
      setLocalMessage('password-mismatch');
    } else {
      const profileData = {
        id: user._id,
        name,
        last_name: lastName,
        email,
        password,
        birth_date: birthDate,
      };
      
      // Add employer fields if user is employer
      if (isEmployer) {
        profileData.company_name = companyName;
        profileData.nip = nip;
        profileData.company_address = companyAddress;
        profileData.company_city = companyCity;
        profileData.company_phone = companyPhone;
      }
      
      dispatch(updateUserProfile(profileData));
      setPassword('');
      setConfirmPassword('');
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation.toLowerCase() !== 'usuń') {
      alert('Wpisz "usuń" aby potwierdzić');
      return;
    }

    const confirmDelete = window.confirm(
      'Czy na pewno chcesz usunąć swoje konto? Ta operacja jest nieodwracalna i wszystkie Twoje dane zostaną trwale usunięte.'
    );

    if (!confirmDelete) {
      return;
    }

    try {
      setDeletingAccount(true);
      await api.delete('/users/profile/delete/');
      
      // Clear localStorage
      localStorage.removeItem('userInfo');
      localStorage.removeItem('authToken');
      
      // Redirect to login
      alert('Konto zostało usunięte');
      navigate('/login');
    } catch (error) {
      alert('Błąd usuwania konta: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeletingAccount(false);
      setShowDeleteModal(false);
      setDeleteConfirmation('');
    }
  };

  if (!userInfo) {
    return null;
  }

  return (
    <div style={{
      minHeight: '100vh',
      paddingTop: '2rem',
      paddingBottom: '4rem',
      overflowX: 'clip',
      width: '100%',
      display: 'flex',
      alignItems: 'center'
    }}>
      <Container>
        <Row className="justify-content-center">
          <Col xs={12} md={10} lg={8} xl={6}>
            
            {/* HERO */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-4"
            >
              <h1 className="pub-h2 mb-2">
                Mój profil
              </h1>
              <p className="pub-muted" style={{ fontSize: '1.2rem' }}>
                Zarządzaj swoimi danymi osobowymi
              </p>
              <p className="pub-muted" style={{ fontSize: '1.2rem' }}>
                Uzupełnienie danych pozwoli Ci na szybsze korzystanie ze wszystkich możliwości jakie daje SigmaWork.
              </p>
            </motion.div>
            {/* SUBSKRYPCJA */}
            {user.subscription && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center mb-4"
              >
                <span
                  className="badge px-4 py-2 fw-bold"
                  style={{
                    background:
                      user.subscription.plan.toLowerCase() === 'darmowy'
                        ? '#6c757d' // szary
                        : 'var(--pub-navy)', // niebieski
                    color: 'white',
                    borderRadius: '20px',
                    fontSize: '1rem',
                    boxShadow: 'none',
                  }}
                > 
                  {user.subscription.plan}{' '}
                  {user.subscription.plan.toLowerCase() !== 'darmowy' && (
                    <>
                      —{' '}
                      {user.subscription.end_date
                        ? `${Math.ceil(
                            (new Date(user.subscription.end_date) - new Date()) /
                              (1000 * 60 * 60 * 24)
                          )} dni pozostało`
                        : 'Bez limitu'}
                    </>
                  )}
                </span>
              </motion.div>
            )}

            {/* KOMUNIKATY */}
            {localMessage && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-3"
              >
                {localMessage === 'success' && (
                  <Alert variant="success" className="border-0" style={{ borderRadius: '15px' }}>
                    ✅ Profil został zaktualizowany
                  </Alert>
                )}
                {localMessage === 'password-mismatch' && (
                  <Alert variant="danger" className="border-0" style={{ borderRadius: '15px' }}>
                    ❌ Hasła nie są takie same
                  </Alert>
                )}
                {localMessage === 'invalid-email' && (
                  <Alert variant="danger" className="border-0" style={{ borderRadius: '15px' }}>
                    ❌ Proszę podać prawidłowy adres email
                  </Alert>
                )}
                {localMessage === 'error' && updateError && (
                  <Alert variant="danger" className="border-0" style={{ borderRadius: '15px' }}>
                    ❌ {updateError}
                  </Alert>
                )}
              </motion.div>
            )}

            {userDetailsError && !loading && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-3"
              >
                <Alert variant="danger" className="border-0" style={{ borderRadius: '15px' }}>
                  ❌ {userDetailsError}
                </Alert>
              </motion.div>
            )}

            {/* KARTA PROFILU - DANE OSOBOWE */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mb-4"
            >
              <Card className="border-0 shadow-lg" style={{ borderRadius: '30px', overflow: 'hidden' }}>
                <Card.Header 
                  className="text-center py-4"
                  style={{
                    background: 'var(--pub-navy)',
                    border: 'none'
                  }}
                >
                  <FaUser size={40} className="mb-2" />
                  <h3 className="fw-bold mb-0">Dane osobowe</h3>
                </Card.Header>

                <Card.Body className="p-4 p-md-5">
                  {loading && <Loader />}

                  <Form onSubmit={submitHandler}>
                    {/* IMIĘ */}
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-medium d-flex align-items-center">
                        <FaUser className="me-2 text-primary" />
                        Imię
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Wpisz imię"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        style={{
                          borderRadius: '15px',
                          border: '2px solid #e9ecef',
                          padding: '0.75rem 1rem',
                          fontSize: '1rem',
                          transition: 'all 0.3s ease'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#667eea'}
                        onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                      />
                    </Form.Group>

                    {/* NAZWISKO */}
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-medium d-flex align-items-center">
                        <FaUser className="me-2 text-primary" />
                        Nazwisko
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Wpisz nazwisko"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                        style={{
                          borderRadius: '15px',
                          border: '2px solid #e9ecef',
                          padding: '0.75rem 1rem',
                          fontSize: '1rem',
                          transition: 'all 0.3s ease'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#667eea'}
                        onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                      />
                    </Form.Group>

                    {/* EMAIL */}
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-medium d-flex align-items-center">
                        <FaEnvelope className="me-2 text-primary" />
                        Adres e-mail
                      </Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="twoj.email@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        style={{
                          borderRadius: '15px',
                          border: '2px solid #e9ecef',
                          padding: '0.75rem 1rem',
                          fontSize: '1rem',
                          transition: 'all 0.3s ease'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#667eea'}
                        onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                      />
                    </Form.Group>

                    {/* DATA URODZENIA */}
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-medium d-flex align-items-center">
                        <FaCalendarAlt className="me-2 text-primary" />
                        Data urodzenia
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={birthDate}
                        onChange={(e) => setBirthDate(e.target.value)}
                        required
                        style={{
                          borderRadius: '15px',
                          border: '2px solid #e9ecef',
                          padding: '0.75rem 1rem',
                          fontSize: '1rem',
                          transition: 'all 0.3s ease'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#667eea'}
                        onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                      />
                    </Form.Group>

                    {/* SEPARATOR - ZMIEŃ HASŁO */}
                    <div className="position-relative my-4">
                      <hr style={{ borderColor: 'rgba(0,0,0,0.1)' }} />
                      <span 
                        className="position-absolute top-50 start-50 translate-middle px-3"
                        style={{
                          background: 'white',
                          color: '#6c757d',
                          fontSize: '0.9rem'
                        }}
                      >
                        Zmień hasło (opcjonalnie)
                      </span>
                    </div>

                    {/* NOWE HASŁO */}
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-medium d-flex align-items-center">
                        <FaLock className="me-2 text-primary" />
                        Nowe hasło
                      </Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Wpisz nowe hasło"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        style={{
                          borderRadius: '15px',
                          border: '2px solid #e9ecef',
                          padding: '0.75rem 1rem',
                          fontSize: '1rem',
                          transition: 'all 0.3s ease'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#667eea'}
                        onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                      />
                      <Form.Text className="text-muted">
                        <small>Zostaw puste, jeśli nie chcesz zmieniać hasła</small>
                      </Form.Text>
                    </Form.Group>

                    {/* POTWIERDŹ HASŁO */}
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-medium d-flex align-items-center">
                        <FaLock className="me-2 text-primary" />
                        Potwierdź nowe hasło
                      </Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Potwierdź nowe hasło"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        style={{
                          borderRadius: '15px',
                          border: '2px solid #e9ecef',
                          padding: '0.75rem 1rem',
                          fontSize: '1rem',
                          transition: 'all 0.3s ease'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#667eea'}
                        onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                      />
                    </Form.Group>
                  </Form>
                </Card.Body>
              </Card>
            </motion.div>

            {/* KARTA PROFILU - DANE FIRMY (tylko dla pracodawcy) */}
            {isEmployer && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mb-4"
              >
                <Card className="border-0 shadow-lg" style={{ borderRadius: '30px', overflow: 'hidden' }}>
                  <Card.Header 
                    className="text-center py-4"
                    style={{
                      background: 'var(--pub-navy)',
                      border: 'none'
                    }}
                  >
                    <FaBuilding size={40} className="mb-2" />
                    <h3 className="fw-bold mb-0">Dane firmy</h3>
                  </Card.Header>

                  <Card.Body className="p-4 p-md-5">
                    <Form>
                      {/* NAZWA FIRMY */}
                      <Form.Group className="mb-4">
                        <Form.Label className="fw-medium d-flex align-items-center">
                          <FaBuilding 
                            className="me-2 text-primary"
                          />
                          Nazwa firmy
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Wpisz nazwę firmy"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          required
                          style={{
                            borderRadius: '15px',
                            border: '2px solid #e9ecef',
                            padding: '0.75rem 1rem',
                            fontSize: '1rem',
                            transition: 'all 0.3s ease'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#f093fb'}
                          onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                        />
                      </Form.Group>

                      {/* NIP */}
                      <Form.Group className="mb-4">
                        <Form.Label className="fw-medium d-flex align-items-center">
                          <FaFileAlt 
                            className="me-2 text-primary"
                          />
                          NIP
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Wpisz NIP (10 cyfr)"
                          value={nip}
                          onChange={(e) => setNip(e.target.value.replace(/\D/g, '').slice(0, 10))}
                          maxLength="10"
                          required
                          style={{
                            borderRadius: '15px',
                            border: '2px solid #e9ecef',
                            padding: '0.75rem 1rem',
                            fontSize: '1rem',
                            transition: 'all 0.3s ease'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#667eea'}
                          onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                        />
                      </Form.Group>

                      {/* ADRES SIEDZIBY */}
                      <Form.Group className="mb-4">
                        <Form.Label className="fw-medium d-flex align-items-center">
                          <FaMapMarkerAlt 
                            className="me-2 text-primary"
                          />
                          Adres siedziby
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Wpisz adres siedziby"
                          value={companyAddress}
                          onChange={(e) => setCompanyAddress(e.target.value)}
                          style={{
                            borderRadius: '15px',
                            border: '2px solid #e9ecef',
                            padding: '0.75rem 1rem',
                            fontSize: '1rem',
                            transition: 'all 0.3s ease'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#f5576c'}
                          onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                        />
                      </Form.Group>

                      {/* MIASTO SIEDZIBY */}
                      <Form.Group className="mb-4">
                        <Form.Label className="fw-medium d-flex align-items-center">
                          <FaCity 
                            className="me-2 text-primary"
                          />
                          Miasto siedziby
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Wpisz miasto"
                          value={companyCity}
                          onChange={(e) => setCompanyCity(e.target.value)}
                          style={{
                            borderRadius: '15px',
                            border: '2px solid #e9ecef',
                            padding: '0.75rem 1rem',
                            fontSize: '1rem',
                            transition: 'all 0.3s ease'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#4facfe'}
                          onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                        />
                      </Form.Group>

                      {/* TELEFON FIRMY */}
                      <Form.Group className="mb-4">
                        <Form.Label className="fw-medium d-flex align-items-center">
                          <FaPhone 
                            className="me-2 text-primary"
                          />
                          Telefon do firmy
                        </Form.Label>
                        <Form.Control
                          type="tel"
                          placeholder="Wpisz numer telefonu"
                          value={companyPhone}
                          onChange={(e) => setCompanyPhone(e.target.value)}
                          style={{
                            borderRadius: '15px',
                            border: '2px solid #e9ecef',
                            padding: '0.75rem 1rem',
                            fontSize: '1rem',
                            transition: 'all 0.3s ease'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#43e97b'}
                          onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                        />
                      </Form.Group>
                    </Form>
                  </Card.Body>
                </Card>
              </motion.div>
            )}

            {/* PRZYCISK AKTUALIZUJ - POZA FORMĄ */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mb-4"
            >
              <Button 
                onClick={submitHandler}
                className="w-100 py-3 fw-bold"
                style={{
                  borderRadius: '15px',
                  border: 'none',
                  background: 'var(--pub-navy)',
                  fontSize: '1.1rem',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 5px 15px rgba(102, 126, 234, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'var(--pub-navy)';
                  e.target.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'var(--pub-navy)';
                  e.target.style.boxShadow = '0 5px 15px rgba(102, 126, 234, 0.3)';
                }}
              >
                Aktualizuj profil
              </Button>
            </motion.div>

            {/* DANGER ZONE */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="mt-5"
            >
              <Card 
                className="border-0 shadow-lg"
                style={{
                  borderRadius: '30px',
                  overflow: 'hidden',
                  borderLeft: '5px solid #dc3545'
                }}
              >
                <Card.Header 
                  className="text-center py-4"
                  style={{
                    background: 'var(--pub-navy)',
                    border: 'none'
                  }}
                >
                  <div className="d-flex align-items-center justify-content-center">
                    <FaExclamationTriangle size={30} className="me-3" />
                    <h3 className="fw-bold mb-0">Strefa niebezpieczna</h3>
                  </div>
                </Card.Header>

                <Card.Body className="p-4 p-md-5">
                  <p className="pub-muted mb-4" style={{ fontSize: '1rem' }}>
                    Operacje w tej sekcji są nieodwracalne. Proszę działać ostrożnie.
                  </p>

                  <div 
                    style={{
                      background: 'rgba(220, 53, 69, 0.1)',
                      border: '2px solid #dc3545',
                      borderRadius: '15px',
                      padding: '1.5rem',
                      marginBottom: '1.5rem'
                    }}
                  >
                    <h5 className="fw-bold mb-2">
                      <FaTrash className="me-2" style={{ color: '#dc3545' }} />
                      Usuń konto
                    </h5>
                    <p className="pub-muted mb-3">
                      Usunięcie konta spowoduje trwałe usunięcie wszystkich Twoich danych z systemu. Tej operacji nie można cofnąć.
                    </p>
                    <Button
                      variant="danger"
                      className="fw-bold"
                      onClick={() => setShowDeleteModal(true)}
                      style={{
                        borderRadius: '12px',
                        padding: '0.6rem 1.5rem',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#bb2d3b';
                        e.target.style.boxShadow = '0 5px 15px rgba(220, 53, 69, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#dc3545';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <FaTrash className="me-2" />
                      Usuń moje konto
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </motion.div>


          </Col>
        </Row>
      </Container>

      {/* Delete Account Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton style={{ background: '#f8f9fa', borderBottom: '2px solid #dc3545' }}>
          <Modal.Title className="text-danger fw-bold">
            <FaExclamationTriangle className="me-2" />
            Usuń konto
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#fff' }}>
          <Alert variant="danger" className="mb-3">
            <Alert.Heading className="fw-bold mb-2">Uwaga!</Alert.Heading>
            <p className="mb-0">
              Ta operacja jest <strong>nieodwracalna</strong>. Wszystkie Twoje dane będą trwale usunięte z systemu i nie będzie możliwe ich przywrócenie.
            </p>
          </Alert>
          
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold text-dark">
              Aby potwierdzić, wpisz poniżej: <span className="text-danger">usuń</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Wpisz: usuń"
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              style={{
                borderRadius: '10px',
                border: '2px solid #e9ecef',
                padding: '0.75rem 1rem'
              }}
              disabled={deletingAccount}
            />
            <Form.Text className="text-muted">
              Potwierdzenie rozróżnia małe i wielkie litery
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ background: '#f8f9fa', borderTop: '2px solid #e9ecef' }}>
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeleteModal(false);
              setDeleteConfirmation('');
            }}
            disabled={deletingAccount}
            style={{ borderRadius: '10px' }}
          >
            Anuluj
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteAccount}
            disabled={deletingAccount || deleteConfirmation.toLowerCase() !== 'usuń'}
            style={{ borderRadius: '10px' }}
          >
            {deletingAccount ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Usuwanie...
              </>
            ) : (
              <>
                <FaTrash className="me-2" />
                Usuń konto
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default UserProfile;
